<template>
  <div>
    <v-card class="pa-3">
      <div class="pb-2">
        <v-layout wrap>
          <v-flex xs8>
            <div class="primary--text">{{ intentName }}</div>
          </v-flex>
          <v-flex xs4 justify-end style="display: flex">
            <v-btn flat icon @click="$emit('close')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-flex>
          <v-flex xs8>
            <v-btn dark depressed color="primary" @click="showFilterDate = !showFilterDate">
              <span class="pr-2">{{ currentDateFilter }}</span>
              <em v-if="!showFilterDate" class="fas fa-angle-down" />
              <em v-else class="fas fa-angle-up" />
            </v-btn>
          </v-flex>
          <v-flex xs4 justify-end style="display: flex">
            <v-btn dark outline color="primary" @click="satisfactionDetailDialogOpen = true">
              {{ $t('satisfaction_detail') }}
            </v-btn>
            <v-btn dark outline color="primary" @click="handleExportXlsx">
              <em class="fas fa-file-export" />&nbsp;{{ $t('export') }}
            </v-btn>
          </v-flex>
        </v-layout>
      </div>

      <div v-if="showFilterDate">
        <v-layout wrap class="form-control">
          <v-flex xs12 sm6 class="px-2">
            <div class="caption">Start date</div>
            <DatepickerCustomDialog
              v-model="filters.start_date"
              :allowed-dates="allowedDates"
              @change="handleChangeStartDate"
            >
              <input
                type="text"
                :value="handleGetDateFormatted(filters.start_date)"
                placeholder="Start date"
                readonly
              />
            </DatepickerCustomDialog>
          </v-flex>
          <v-flex xs12 sm6 class="px-2">
            <div class="caption">End date</div>
            <DatepickerCustomDialog
              v-model="filters.end_date"
              :allowed-dates="allowedDates"
              @change="handleChangeEndDate"
            >
              <input
                type="text"
                :value="handleGetDateFormatted(filters.end_date)"
                placeholder="End date"
                readonly
              />
            </DatepickerCustomDialog>
          </v-flex>
        </v-layout>
      </div>

      <template v-if="rawData">
        <div>
          <SatisfactionList :prefix="'Q1'" :items="rawData.question_1" />
        </div>
        <div class="mt-3">
          <v-list class="py-0">
            <v-list-tile style="border-bottom: 1px solid #ccc; background: #f3f7ff">
              <v-layout wrap class="font-weight-bold">
                <v-flex xs1>Q3</v-flex>
                <v-flex xs6>ข้อเสนอแนะเพิ่มเติม</v-flex>
                <v-flex xs5 class="text-xs-center">{{
                  (rawData.suggestion && rawData.suggestion.count) || 0
                }}</v-flex>
              </v-layout>
            </v-list-tile>
          </v-list>
        </div>
      </template>
    </v-card>

    <v-dialog v-model="satisfactionDetailDialogOpen" persistent width="1000px">
      <SatisfactionDetail
        v-if="satisfactionDetailDialogOpen"
        :date-filter="dateFilter"
        :intent-name="intentName"
        @close="satisfactionDetailDialogOpen = false"
      />
    </v-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import generaliApi from '@/api/generali'
import { mapGetters } from 'vuex'
import SatisfactionList from './SatisfactionList.vue'
import { DATETIME } from '@/constants/datetime.js'
import DatepickerCustomDialog from '@/components/elements/DatepickerCustomDialog.vue'
import SatisfactionDetail from './SatisfactionDetail'

export default {
  components: {
    SatisfactionList,
    DatepickerCustomDialog,
    SatisfactionDetail
  },
  props: {
    intentName: {
      type: String,
      default: ''
    },
    dateFilter: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      rawData: null,
      showFilterDate: false,
      filters: {
        start_date: dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
        end_date: dayjs().format('YYYY-MM-DD')
      },
      satisfactionDetailDialogOpen: false
    }
  },
  computed: {
    ...mapGetters(['selectedBot']),
    currentDateFilter() {
      const startDate = dayjs(this.filters.start_date).format('DD/MM/YYYY')
      const endDate = dayjs(this.filters.end_date).format('DD/MM/YYYY')
      return `${startDate} - ${endDate}`
    }
  },
  watch: {
    dateFilter: {
      deep: true,
      handler() {
        this.filters = {
          ...this.filters,
          start_date: this.dateFilter.start_date,
          end_date: this.dateFilter.end_date
        }
      }
    },
    filters: {
      deep: true,
      handler() {
        this.handleFetchSatisfactionScoreByIntent()
      }
    }
  },
  mounted() {
    if (this.intentName && this.dateFilter) {
      this.filters = {
        ...this.filters,
        start_date: this.dateFilter.start_date,
        end_date: this.dateFilter.end_date
      }
    }
  },
  methods: {
    async handleFetchSatisfactionScoreByIntent() {
      const vm = this
      try {
        const data = {
          page_id: vm.selectedBot.pageid,
          start_date: vm.filters.start_date,
          end_date: vm.filters.end_date,
          intent: vm.intentName
        }

        vm.$store.dispatch('loading')
        const response = await generaliApi.getIntentSatisfactionSurveyDetail({
          config: { params: data }
        })
        vm.$store.dispatch('clear_loading')

        if (response && response.status === 200) {
          const responseData = response.data
          vm.rawData = responseData
        }
      } catch (error) {
        console.error(error)
      } finally {
        vm.$store.dispatch('clear_loading')
      }
    },
    async handleExportXlsx() {
      const vm = this
      try {
        vm.$store.dispatch('loading')
        const exportRawData = vm.rawData

        if (!exportRawData) return

        const json = []

        json.push([`${vm.intentName}`])
        json.push(['Date: ', `${vm.currentDateFilter}`])
        json.push([])
        json.push(['คำถาม', 'จำนวน'])
        json.push(['Q1 คุณรู้สึกชื่นชอบการให้บริการครั้งนี้แค่ไหน'])

        Object.entries(exportRawData.question_1).map((item) => {
          if (item[0] !== 'name') {
            json.push([`${vm.getScoreText(item[0])}`, `${item[1]}`])
          }
        })

        json.push([
          'Q3 ข้อเสนอแนะเพิ่มเติม',
          `${(exportRawData.suggestion && exportRawData.suggestion.count) || 0}`
        ])

        const heading = [['', '']]
        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet([])
        XLSX.utils.sheet_add_aoa(ws, heading)
        XLSX.utils.sheet_add_json(ws, json, { origin: 'A1', skipHeader: true })
        XLSX.utils.book_append_sheet(wb, ws)
        XLSX.writeFile(wb, `satisfaction-survey.xlsx`)
      } catch (error) {
        console.error(error)
      } finally {
        vm.$store.dispatch('clear_loading')
      }
    },
    getScoreText(value) {
      let text = ''
      switch (value) {
        case 'count_score_1':
          text = 'ไม่พึงพอใจเลย (1)'
          break
        case 'count_score_2':
          text = 'พึงพอใจน้อย (2)'
          break
        case 'count_score_3':
          text = 'พึงพอใจปานกลาง (3)'
          break
        case 'count_score_4':
          text = 'พึงพอใจมาก (4)'
          break
        case 'count_score_5':
          text = 'พึงพอใจมากที่สุด (5)'
          break

        default:
          text = ''
          break
      }
      return text
    },
    async getExportIntentSatisfactionSurveyDetail() {
      const vm = this
      try {
        const data = {
          page_id: vm.selectedBot.pageid,
          start_date: vm.filters.start_date,
          end_date: vm.filters.end_date,
          intent: vm.intentName
        }

        const response = await generaliApi.getExportIntentSatisfactionSurveyDetail({
          config: { params: data }
        })

        if (response && response.status === 200) {
          return response.data.data
        } else {
          return null
        }
      } catch (error) {
        console.error(error)
        return null
      }
    },
    handleGetDateFormatted(value) {
      return value ? dayjs(value).format(DATETIME.DATE_FORMAT_DISPLAY) : ''
    },
    allowedDates(val) {
      return dayjs().diff(val, 'hour', true) > 0
    },
    handleChangeStartDate(value) {
      if (dayjs(this.filters.end_date).isBefore(value)) {
        this.filters.end_date = value
      }
      this.filters.start_date = value
    },
    handleChangeEndDate(value) {
      if (dayjs(value).isBefore(this.filters.start_date)) {
        this.filters.start_date = value
      }
      this.filters.end_date = value
    }
  }
}
</script>

<style lang="scss" scoped></style>



// WEBPACK FOOTER //
// src/components/view/dashboard/customize/generali/SatisfactionSurveyReport/DialogSatisfactionScore.vue